import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import Header from "../../../components/Header/Header";
import images from "../../../images";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import CheckBoxQuestion from "./CheckBoxQuestion";
import MCQQuestion from "./MCQQuestion";
import YesNoQuestion from "./YesNoQuesttion";
import LongShortQuestion from "./LongShortQuestion";
import SliderQuestion from "./SliderQuestion";
import {
  updateQuestionDefault,
  updateQuestionCustom,
  updateQuestionAI,
  deleteQuestion,
  deleteQuestionDefault,
  deleteQuestionAI,
} from "../../../redux/featuresSlice/commonSlice";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import CommonModal from "../../../components/common/Modal/ModalCommon";
import Button from "../../../components/common/Button/Button";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const Question = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { question } = location.state || {};
  const { objecttive_name } = location.state || {};
  const { isAIGenerated } = location.state || {};

  const queryParams = new URLSearchParams(location.search);
  const hasDefaultQuestionCustome = queryParams.has("defaultQuestionCustome");
  const isCustomQuest = queryParams.has("customQuest");
  const deleteQuestModalRef = useRef();
  const confirmChangeTypeOptionModalRef = useRef();

  const [questionText, setQuestionText] = useState(
    question?.objective_question ||
      question?.question ||
      question?.question_text ||
      ""
  );

  const [questionError, setQuestionError] = useState("");

  const [options, setOptions] = useState(() => {
    if (question?.type === "slider") {
      const minVal = question?.minValue || 1;
      const maxVal = question?.maxValue || 5;
      const range = maxVal - minVal + 1;
      const initialOptions = [];
      for (let i = 0; i < range; i++) {
        const value = minVal + i;
        const text = question?.options?.[i]?.text || `${value}`;
        initialOptions.push({
          id: question?.options?.[i]?.id || uuidv4(),
          text,
          value: `${value}`,
        });
      }
      return initialOptions;
    }
    const initialOptions = question?.options || question?.option || [];
    return initialOptions.map((opt) => ({
      ...opt,
      id: opt.id || uuidv4(),
      text: opt.text || opt.option_text || "",
    }));
  });

  const [yesNoOptions, setYesNoOptions] = useState([
    { id: uuidv4(), text: "Yes" },
    { id: uuidv4(), text: "No" },
  ]);
  const [selectedYesNoOption, setSelectedYesNoOption] = useState(null);
  const [commentPlaceholder, setCommentPlaceholder] = useState(
    question?.additionalComments || ""
  );
  const [isRequired, setIsRequired] = useState(
    question?.required !== undefined ? question.required : true
  );
  const [isAdditionalComments, setIsAdditionalComments] = useState(
    question?.isAdditionalComments ? true : false
  );
  const [answerText, setAnswerText] = useState(question?.answer || "");
  const [isCompulsory, setIsCompulsory] = useState(
    question?.compulsory !== undefined ? question.compulsory : true
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [questionType, setQuestionType] = useState({
    value: question?.type || "checkbox",
    label: question?.type || "checkbox",
  });

  const questionTypes = useMemo(
    () => [
      { value: "mcq", label: "MCQ" },
      { value: "yes_no", label: "Yes/No" },
      { value: "checkbox", label: "Checkbox" },
      { value: "short", label: "Short" },
      { value: "long", label: "Long" },
      { value: "slider", label: "Slider" },
    ],
    []
  );

  useEffect(() => {
    if (question?.objective_question || question?.question) {
      setQuestionText(
        question.objective_question ||
          question.question ||
          question.question_text
      );
    }
    if (question?.options?.length > 0 || question?.option?.length > 0) {
      const updatedOptions = (question.options || question.option).map(
        (opt) => ({
          ...opt,
          id: opt.id || uuidv4(),
        })
      );
      setOptions(updatedOptions);
    }
  }, [question]);

  useEffect(() => {
    const typeFromUrl = searchParams.get("type");
    if (typeFromUrl) {
      const matchedType = questionTypes.find((t) => t.value === typeFromUrl);
      if (matchedType) {
        setQuestionType(matchedType);
      }
    }
  }, [searchParams, questionTypes]);

  const handleTypeSelect = (type) => {
    const typesRequiringConfirmation = ["checkbox", "mcq"];
    if (
      type.value !== questionType.value &&
      typesRequiringConfirmation.includes(type.value) &&
      typesRequiringConfirmation.includes(questionType.value) &&
      options.length > 0 // Only show modal if options exist
    ) {
      confirmChangeTypeOptionModalRef.current.openModal();
      setQuestionType(type);
    } else {
      setQuestionType(type);
      setDropdownOpen(false);
      if (type.value === "slider") {
        // Initialize default options for slider
        const defaultOptions = [
          { id: uuidv4(), text: "Option 1", value: "1" },
          { id: uuidv4(), text: "Option 2", value: "2" },
          { id: uuidv4(), text: "Option 3", value: "3" },
          { id: uuidv4(), text: "Option 4", value: "4" },
          { id: uuidv4(), text: "Option 5", value: "5" },
        ];
        setOptions(defaultOptions);
      }
    }
  };

  const handleNextClick = useCallback(() => {
    if (!questionText.trim()) {
      setQuestionError(t("base.questionCannotBeEmpty"));
      return;
    }
    if (questionType.value === "checkbox" || questionType.value === "mcq") {
      if (options.length === 0) {
        toast.error(t("quest.pleaseSelectAtLeastOneOption"));
        return;
      }
    }

    setQuestionError("");
    const formData = {
      question: questionText,
      createdAt: question.createdAt ? question.createdAt : Date.now(),
      type: questionType.value,
      required: isRequired,
      questionId: question?.id || question?.questionId || uuidv4(),
      additionalComments: isAdditionalComments ? commentPlaceholder : null,
      options: options,
      isAdditionalComments: isAdditionalComments,
    };

    if (questionType.value !== "short" && questionType.value !== "long") {
      formData.compulsory = isCompulsory;
      formData.isAdditionalComments = isAdditionalComments;
    }

    switch (questionType.value) {
      case "checkbox":
        formData.selectedOptions = options
          .filter((opt) => opt.checked)
          .map((opt) => (opt.text ? opt.text : opt.option_text));
        break;
      case "slider":
        formData.options = options.map((opt, index) => ({
          ...opt,
          value: opt.value,
          option_text: opt.option_text ? opt.option_text : opt.text,
        }));
        formData.minValue = 1;
        formData.maxValue = options.length;
        formData.minLabel = options[0]?.text || "";
        formData.maxLabel = options[options.length - 1]?.text || "";
        break;
      case "mcq":
        formData.selectedOption =
          options.find((opt) => opt.checked)?.text || null;
        break;
      case "yes_no":
        formData.options = yesNoOptions;
        formData.selectedOption = selectedYesNoOption
          ? yesNoOptions.find((opt) => opt.id === selectedYesNoOption)?.text
          : null;
        break;
      case "short":
      case "long":
        formData.answer = answerText;
        break;
      default:
        break;
    }

    if (isAIGenerated) {
      dispatch(updateQuestionAI(formData));
    } else if (isCustomQuest) {
      dispatch(updateQuestionCustom(formData));
    } else if (hasDefaultQuestionCustome) {
      dispatch(updateQuestionDefault(formData));
    } else {
      dispatch(updateQuestionCustom(formData));
    }

    navigate(
      `/ondemand?step=2&objectiveId=${
        question?.objective_id || ""
      }&objectiveName=${
        question?.objecttive_name || objecttive_name || ""
      }&customQuestMaual`,
      { state: { from: "questionData" } }
    );
  }, [
    questionText,
    questionType,
    isRequired,
    isAdditionalComments,
    commentPlaceholder,
    options,
    yesNoOptions,
    selectedYesNoOption,
    answerText,
    isCompulsory,
    isCustomQuest,
    hasDefaultQuestionCustome,
    isAIGenerated,
    question,
    navigate,
    dispatch,
    objecttive_name,
    t,
  ]);

  const onBack = () => {
    navigate(
      `/ondemand?step=2&objectiveId=${
        question?.objective_id || ""
      }&objectiveName=${question?.objecttive_name || objecttive_name || ""}`
    );
  };

  const handleDeleteQuest = () => {
    if (question) {
      deleteQuestModalRef.current?.openModal();
    }
  };

  const onDeleteQuest = () => {
    if (!question) {
      console.warn("No question to delete");
      return;
    }

    const questionIdToDelete = question.questionId || question.id;
    if (!questionIdToDelete) {
      console.error("Question ID is undefined, cannot delete");
      return;
    }
    if (isAIGenerated) {
      dispatch(deleteQuestionAI(questionIdToDelete));
    } else if (isCustomQuest) {
      dispatch(deleteQuestion(questionIdToDelete));
    } else {
      dispatch(deleteQuestionDefault(questionIdToDelete));
    }

    deleteQuestModalRef.current?.closeModal();
    navigate(
      `/ondemand?step=2&objectiveId=${question?.objective_id}&objectiveName=${
        question?.objecttive_name ? question?.objecttive_name : objecttive_name
      }`
    );
  };

  const handleConfirmChangeTypeOption = () => {
    if (questionType.value === "slider") {
      // Initialize default options for slider
      const defaultOptions = [
        { id: uuidv4(), text: "Option 1", value: "1" },
        { id: uuidv4(), text: "Option 2", value: "2" },
        { id: uuidv4(), text: "Option 3", value: "3" },
        { id: uuidv4(), text: "Option 4", value: "4" },
        { id: uuidv4(), text: "Option 5", value: "5" },
      ];
      setOptions(defaultOptions);
    } else {
      setOptions([]);
    }
    confirmChangeTypeOptionModalRef.current.closeModal();
    setDropdownOpen(false);
  };
  const handleCacelConfirmChangeTypeOption = () => {
    if (questionType.value === "slider" && options.length === 0) {
      // Initialize default options for slider
      const defaultOptions = [
        { id: uuidv4(), text: "Option 1", value: "1" },
        { id: uuidv4(), text: "Option 2", value: "2" },
        { id: uuidv4(), text: "Option 3", value: "3" },
        { id: uuidv4(), text: "Option 4", value: "4" },
        { id: uuidv4(), text: "Option 5", value: "5" },
      ];
      setOptions(defaultOptions);
    }
    confirmChangeTypeOptionModalRef.current.closeModal();
    setDropdownOpen(false);
  };
  const renderQuestionComponent = () => {
    switch (questionType.value) {
      case "checkbox":
        return (
          <CheckBoxQuestion
            options={options}
            setOptions={setOptions}
            isRequired={isRequired}
            setIsRequired={setIsRequired}
            isAdditionalComments={isAdditionalComments}
            setIsAdditionalComments={setIsAdditionalComments}
            commentPlaceholder={commentPlaceholder}
            setCommentPlaceholder={setCommentPlaceholder}
            isCompulsory={isCompulsory}
            setIsCompulsory={setIsCompulsory}
          />
        );
      case "mcq":
        return (
          <MCQQuestion
            options={options}
            setOptions={setOptions}
            isRequired={isRequired}
            setIsRequired={setIsRequired}
            isAdditionalComments={isAdditionalComments}
            setIsAdditionalComments={setIsAdditionalComments}
            commentPlaceholder={commentPlaceholder}
            setCommentPlaceholder={setCommentPlaceholder}
            isCompulsory={isCompulsory}
            setIsCompulsory={setIsCompulsory}
          />
        );
      case "yes_no":
        return (
          <YesNoQuestion
            options={yesNoOptions}
            setOptions={setYesNoOptions}
            isRequired={isRequired}
            setIsRequired={setIsRequired}
            isAdditionalComments={isAdditionalComments}
            setIsAdditionalComments={setIsAdditionalComments}
            commentPlaceholder={commentPlaceholder}
            setCommentPlaceholder={setCommentPlaceholder}
            selectedOption={selectedYesNoOption}
            setSelectedOption={setSelectedYesNoOption}
            isCompulsory={isCompulsory}
            setIsCompulsory={setIsCompulsory}
          />
        );
      case "short":
      case "long":
        return (
          <LongShortQuestion
            isRequired={isRequired}
            setIsRequired={setIsRequired}
            questionType={questionType}
            answerText={answerText}
            setAnswerText={setAnswerText}
          />
        );
      case "slider":
        return (
          <SliderQuestion
            isRequired={isRequired}
            setIsRequired={setIsRequired}
            options={options}
            setOptions={setOptions}
            isAdditionalComments={isAdditionalComments}
            setIsAdditionalComments={setIsAdditionalComments}
            commentPlaceholder={commentPlaceholder}
            setCommentPlaceholder={setCommentPlaceholder}
            isCompulsory={isCompulsory}
            setIsCompulsory={setIsCompulsory}
          />
        );
      default:
        return null;
    }
  };

  return (
    <main className="flex flex-col bg-neutral-100 min-h-screen">
      <div className="sticky top-0 left-0 w-full bg-[#3F54DF] z-50">
        <Header
          title={t("quest.question")}
          backIcon={images.BackIcon}
          onBackClick={onBack}
          textColor="text-white"
        />
      </div>

      <div className="p-4">
        <div className="bg-white p-4 rounded-lg mb-4">
          <textarea
            value={questionText}
            placeholder={t("quest.inputYourQuestionHere")}
            onChange={(e) => {
              setQuestionText(e.target.value);
              if (e.target.value.trim()) {
                setQuestionError("");
              }
            }}
            className={`w-full resize-none border-0 focus:ring-0 text-base focus:outline-none ${
              questionError ? "border border-red-500" : ""
            }`}
            rows={3}
          />
        </div>
        {questionError && (
          <p className="text-red-500 text-sm mt-1">{questionError}</p>
        )}
        <div className="bg-neutral-100">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-medium text-indigo-600">
              {t("base.options")}
            </span>
            <div className="relative">
              <div
                className="bg-amber-200 text-gray-800 px-4 py-1 rounded-md flex justify-between items-center cursor-pointer"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <span>
                  {questionType.label === "yes_no"
                    ? "Yes/No"
                    : questionType.label}
                </span>
                <img
                  src={images.ChevronDownIcon}
                  alt=""
                  className="w-4 h-4 ml-2"
                />
              </div>
              {dropdownOpen && (
                <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  {questionTypes.map((type) => (
                    <div
                      key={type.value}
                      className="px-5 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleTypeSelect(type)}
                    >
                      {type.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {renderQuestionComponent()}
        </div>

        <div className="flex justify-center mt-4">
          <button
            onClick={handleNextClick}
            className="w-full bg-[#3F54DF] text-white py-3 rounded-full font-medium"
          >
            {t("base.done")}
          </button>
        </div>

        <div className="flex justify-center mt-4">
          <button
            onClick={handleDeleteQuest}
            className="w-full bg-white border border-solid border-red-600 text-red-600 py-3 rounded-full font-medium"
            disabled={!question}
          >
            {t("base.delete")}
          </button>
        </div>
      </div>
      <CommonModal
        ref={deleteQuestModalRef}
        title={t("quest.areYouSureYouWantToDeleteThisQuestion")}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.cancel")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] bg-white border border-solid border-stone-200 text-neutral-500"
              onClick={() => closeModal(false)}
            />
            <Button
              text={t("base.delete")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] text-white bg-[#EF4056]"
              variant="primary"
              onClick={onDeleteQuest}
            />
          </>
        )}
      >
        <div className="flex justify-center items-center">
          <img src={images.TrashIconRed} alt="" className="w-[32px]" />
        </div>
      </CommonModal>

      <CommonModal
        ref={confirmChangeTypeOptionModalRef}
        actions={(closeModal) => (
          <>
            <Button
              text={t("base.clear")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] bg-white border border-solid border-stone-200 text-neutral-500"
              variant="primary"
              onClick={handleConfirmChangeTypeOption}
            />
            <Button
              text={t("base.keep")}
              className="flex-1 shrink gap-2.5 self-stretch px-5 py-3 rounded-xl min-h-[40px] text-white bg-indigo-600"
              onClick={handleCacelConfirmChangeTypeOption}
            />
          </>
        )}
      >
        <div className="flex flex-col justify-between gap-2">
          <p className="text-sm">
            {t("quest.onDemand.doYouWantTokeepTheCurrentOptions")}
          </p>
        </div>
      </CommonModal>
    </main>
  );
};

export default Question;
